import { apiClient, API_ENDPOINTS } from '../config/api';
import { ProfileImageDto, PresignedUrlResponse } from '../types';

export interface ImageUploadResult {
  success: boolean;
  profileImage?: ProfileImageDto;
  error?: string;
}

export interface UploadProgress {
  fileIndex: number;
  fileName: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

/**
 * 이미지 파일 유효성 검사
 */
export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  // 파일 크기 체크 (10MB)
  const MAX_SIZE = 10 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    return { valid: false, error: `파일 크기가 10MB를 초과합니다. (${(file.size / 1024 / 1024).toFixed(1)}MB)` };
  }

  // MIME 타입 체크
  const allowedTypes = [
    'image/jpeg', 
    'image/jpg', 
    'image/png', 
    'image/webp',
    // 아이폰 특화 포맷
    'image/heic', 
    'image/heif',
    // 안드로이드/기타 포맷
    'image/avif',  // 차세대 이미지 포맷
    'image/bmp',   // 기본 비트맵
    'image/gif',   // GIF (움직이는 이미지 포함)
    'image/tiff',  // 고품질 이미지
    'image/svg+xml' // 벡터 이미지
  ];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: `지원하지 않는 파일 형식입니다. (${file.type})` };
  }

  return { valid: true };
};

/**
 * 이미지 파일 리스트 유효성 검사
 */
export const validateImageFiles = (files: File[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // 각 파일 유효성 검사 (개수 체크는 컴포넌트에서 총합으로 처리)
  files.forEach((file, index) => {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      errors.push(`사진 ${index + 1}: ${validation.error}`);
    }
  });

  return { valid: errors.length === 0, errors };
};

/**
 * Presigned URL 요청
 */
export const getPresignedUrl = async (fileName: string, contentType: string): Promise<PresignedUrlResponse> => {
  const url = `${API_ENDPOINTS.PROFILE.UPLOAD_URL}?fileName=${encodeURIComponent(fileName)}&contentType=${encodeURIComponent(contentType)}`;
  const response: any = await apiClient.post(url);
  return response.result || response;
};

/**
 * S3에 파일 업로드
 */
export const uploadToS3 = async (
  file: File, 
  presignedData: PresignedUrlResponse,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    
    // Presigned fields를 먼저 추가
    Object.entries(presignedData.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    // 파일을 마지막에 추가
    formData.append('file', file);

    const xhr = new XMLHttpRequest();

    // 진행률 추적
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * 100) / event.total);
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('POST', presignedData.url);
    xhr.send(formData);
  });
};

/**
 * 단일 이미지 업로드 (Presigned URL 요청 + S3 업로드)
 */
export const uploadSingleImage = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<ProfileImageDto> => {
  // 파일 유효성 검사
  const validation = validateImageFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // UUID + 원본파일명으로 고유한 파일명 생성
  const uniqueFileName = `${crypto.randomUUID()}_${file.name}`;

  try {
    // 1. Presigned URL 요청
    const presignedData = await getPresignedUrl(uniqueFileName, file.type);

    // 2. S3 업로드
    await uploadToS3(file, presignedData, onProgress);

    // 3. ProfileImageDto 반환
    return {
      storageKey: presignedData.fields.key,
      mimeType: file.type,
      size: file.size
    };
  } catch (error) {
    console.error('이미지 업로드 실패:', error);
    throw error;
  }
};

/**
 * 다중 이미지 업로드
 */
export const uploadMultipleImages = async (
  files: File[],
  onProgress?: (progresses: UploadProgress[]) => void
): Promise<ProfileImageDto[]> => {
  // 파일 리스트 유효성 검사
  const validation = validateImageFiles(files);
  if (!validation.valid) {
    throw new Error(validation.errors.join('\n'));
  }

  const results: ProfileImageDto[] = [];
  const progresses: UploadProgress[] = files.map((file, index) => ({
    fileIndex: index,
    fileName: file.name,
    progress: 0,
    status: 'pending'
  }));

  // 진행 상황 업데이트 함수
  const updateProgress = (index: number, updates: Partial<UploadProgress>) => {
    progresses[index] = { ...progresses[index], ...updates };
    if (onProgress) {
      onProgress([...progresses]);
    }
  };

  // 순차적으로 업로드 (서버 부하 고려)
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      updateProgress(i, { status: 'uploading', progress: 0 });

      const profileImage = await uploadSingleImage(file, (progress) => {
        updateProgress(i, { progress });
      });

      updateProgress(i, { status: 'completed', progress: 100 });
      results.push(profileImage);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '업로드 실패';
      updateProgress(i, { status: 'error', error: errorMessage });
      throw new Error(`${file.name} 업로드 실패: ${errorMessage}`);
    }
  }

  return results;
};